import {
  ReactElement,
  useEffect,
  useState,
  Fragment,
  useCallback,
  useMemo,
} from "react";
import { Menu, Popover, Transition } from "@headlessui/react";
import MainLayout from "../../components/layout/main";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import { useAuth } from "../../hooks/auth";
import Unprotected from "../../server/unprotected";
import classNames from "../../util/classNames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AcademicCapIcon,
  BadgeCheckIcon,
  BellIcon,
  CashIcon,
  ClockIcon,
  LocationMarkerIcon,
  MenuIcon,
  ReceiptRefundIcon,
  UsersIcon,
  XIcon,
} from "@heroicons/react/outline";
import { StarIcon, SearchIcon } from "@heroicons/react/solid";
import { useApi } from "../../hooks/api";
import { Activity, ActivityType } from "../../model/activity";
import { Review } from "../../model/review";
import {
  PrepCenter,
  PrepCenterLocation,
  SupportedModel,
  Turnaround,
} from "../../model/prepCenter";
import Spinner from "../../components/spinner";
import ConfirmationModal from "../../components/modal/confirmation";
import CreateReviewModal from "../../components/modal/createReview";
import UpdateReviewModal from "../../components/modal/updateReview";
import Link from "next/link";
import Image from "next/image";
import { NextSeo } from "next-seo";

const supportedModelDetails = {
  [SupportedModel.SupportedModelWholesale]: {
    title: "Wholesale",
    description: "High unit volume, often palletized.",
    color: "bg-green-100 text-green-800",
  },
  [SupportedModel.SupportedModelPrivateLabel]: {
    title: "Private label",
    description: "High unit volume, often palletized.",
    color: "bg-blue-100 text-blue-800",
  },
  [SupportedModel.SupportedModelOnlineArbitrage]: {
    title: "Online arbitrage",
    description:
      "Variety of products, ordered online and shipped to the center.",
    color: "bg-purple-100 text-purple-800",
  },
  [SupportedModel.SupportedModelRetailArbitrage]: {
    title: "Retail arbitrage",
    description: "Variety of products.",
    color: "bg-indigo-100 text-indigo-800",
  },
  [SupportedModel.SupportedModelHazmat]: {
    title: "Hazmat",
    description:
      "Dangerous goods that require the seller to have authorization for.",
    color: "bg-red-100 text-red-800",
  },
  [SupportedModel.SupportedModelBooks]: {
    title: "Books",
    description: "New and used book prep.",
    color: "bg-orange-100 text-orange-800",
  },
};

const turnaroundTimes = {
  [Turnaround.TurnaroundUnknown]: {
    title: "Unknown",
  },
  [Turnaround.Turnaround24]: {
    title: "<24",
  },
  [Turnaround.Turnaround24to48]: {
    title: "24-48",
  },
  [Turnaround.Turnaround48to72]: {
    title: "48-72",
  },
  [Turnaround.Turnaround72plus]: {
    title: "72+",
  },
};

const formatWebsite = (website: string | undefined | null): string | null => {
  if (!website) return null;

  const match = website.match(/^(?:f|ht)tps?\:\/\//);
  if (!match) {
    return "https://" + website;
  }

  return website;
};

const formatPhone = (phone: string | undefined | null): string | null => {
  if (!phone) return null;

  const cleaned = phone.replace(/\D/g, "");
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    var intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }

  return null;
};

const randomGradientBg = (): string => {
  const gradients: string[] = [
    "from-cyan-500 to-blue-500",
    "from-sky-500 to-indigo-500",
    "from-blue-500 to-cyan-500",
    "from-indigo-500 to-sky-500",
    // "from-violet-500 to-fuchsia-500",
    // "from-purple-500 to-pink-500",
  ];

  return gradients[Math.floor(Math.random() * gradients.length)];
};

const getInitials = (review: Review): string => {
  if (review.reviewerFirstName && review.reviewerLastName) {
    return (
      review.reviewerFirstName.charAt(0).toUpperCase() +
      review.reviewerLastName.charAt(0).toUpperCase()
    );
  }

  return review.reviewerName?.slice(0, 2).toUpperCase() ?? "PC";
};

const getLocationString = (location: PrepCenterLocation): string => {
  let locationString = "";

  if (location.line1) locationString += location.line1 + ", ";
  if (location.line2) locationString += location.line2 + ", ";
  if (location.city) locationString += location.city + ", ";
  locationString += location.state + ", ";
  if (location.postalCode) locationString += location.postalCode + ", ";
  locationString += location.country;

  return locationString;
};

export default function Home() {
  const router = useRouter();
  const { prepCenterId } = router.query;
  const { authUser, dbUser, getOrCreateUser } = useAuth();
  const { getPrepCenter, claimPrepCenter } = useApi();
  const [prepCenter, setPrepCenter] = useState<PrepCenter | null>(null);
  const [loading, setLoading] = useState(true);
  const [prepCenterError, setPrepCenterError] = useState<string | null>(null);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [createReviewModalOpen, setCreateReviewModalOpen] = useState(false);
  const [updateReviewModalOpen, setUpdateReviewModalOpen] = useState(false);

  const myReview = useMemo((): Review | null => {
    if (!authUser || !prepCenter || !prepCenter.reviews) return null;
    for (let i = 0; i < prepCenter.reviews.length; i++) {
      if (prepCenter.reviews[i].userId === authUser.id) {
        return prepCenter.reviews[i];
      }
    }
    return null;
  }, [prepCenter, authUser]);

  const doGetPrepCenter = useCallback(async () => {
    if (!prepCenterId) return;
    const { status, prepCenter, errorMessage } = await getPrepCenter(
      prepCenterId as string
    );
    if (status === 200) {
      setPrepCenter(prepCenter as PrepCenter);
    } else {
      setPrepCenterError(errorMessage as string);
    }
    setLoading(false);
  }, [
    setPrepCenterError,
    getPrepCenter,
    setLoading,
    setPrepCenter,
    prepCenterId,
  ]);

  useEffect(() => {
    doGetPrepCenter();
  }, [doGetPrepCenter]);

  const handleClaimPrepCenter = useCallback(async () => {
    const { status } = await claimPrepCenter(prepCenter?.id as string, {
      toastError: true,
      toastSuccess: true,
    });
    if (status === 200) {
      getOrCreateUser(true);
      router.push("/manage");
    }
  }, [claimPrepCenter, router, prepCenter, getOrCreateUser]);

  return (
    <>
      {prepCenter && (
        <NextSeo
          title={`${prepCenter.name} | Find an Amazon FBA Prep Center`}
        />
      )}

      <header className="bg-vendrive-base pt-8 pb-40"></header>

      <main className="-mt-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
          <div className="grid grid-cols-1 gap-4 items-start lg:grid-cols-3 lg:gap-8">
            <div className="grid grid-cols-1 gap-4 lg:col-span-2">
              {/* Profile */}
              <section aria-labelledby="profile-overview-title">
                <div className="rounded-lg bg-white overflow-hidden shadow">
                  {/* Banner */}
                  <div className="bg-white p-2">
                    <div className="relative h-36 sm:h-48 justify-center items-center flex rounded-md">
                      <div className="absolute z-10 left-3 top-3">
                        <span
                          className={classNames(
                            "inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium",
                            prepCenter?.acceptingClients == null
                              ? "hidden"
                              : "",
                            prepCenter?.acceptingClients
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          )}
                        >
                          {`${
                            prepCenter?.acceptingClients
                              ? "Accepting"
                              : "Not accepting"
                          } clients`}
                        </span>
                      </div>

                      <div
                        className={classNames(
                          "absolute z-10 right-3 top-3",
                          loading ||
                            dbUser?.prep_center_id ||
                            prepCenter?.ownerId != null
                            ? "hidden"
                            : ""
                        )}
                      >
                        {authUser ? (
                          <button
                            onClick={() => setClaimModalOpen(true)}
                            type="button"
                            className="flex justify-center items-center px-4 py-2 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <span>
                              Claim{" "}
                              <span className="hidden sm:inline-block">
                                business
                              </span>
                            </span>
                          </button>
                        ) : (
                          <Link href="/signup">
                            <a className="flex justify-center items-center px-4 py-2 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                              <span>
                                Claim{" "}
                                <span className="hidden sm:inline-block">
                                  business
                                </span>
                              </span>
                            </a>
                          </Link>
                        )}
                      </div>

                      <div
                        className={classNames(
                          "absolute w-full h-full bg-gradient-to-r justify-center items-center flex bg-center bg-no-repeat bg-cover rounded-md",
                          randomGradientBg()
                        )}
                      ></div>

                      <div
                        style={
                          prepCenter?.banner
                            ? {
                                backgroundImage:
                                  `url("${prepCenter?.banner}")` ?? undefined,
                              }
                            : {
                                backgroundImage: `url("/images/banner-placeholder.jpg")`,
                                opacity: 0.1,
                              }
                        }
                        className="absolute opacity-50 w-full h-full bg-center bg-no-repeat bg-cover rounded-md"
                      ></div>
                    </div>
                  </div>

                  <div className="relative bg-white px-6 pb-6 pt-4">
                    <div className="sm:flex sm:items-start sm:justify-between">
                      <div className="-mt-20 z-10">
                        <div className="rounded-lg">
                          {prepCenter?.logo ? (
                            <div className="h-28 w-28 sm:h-40 sm:w-40 rounded-md bg-white p-1 sm:-ml-1 sm:mr-0 mx-auto">
                              <div className="relative flex h-full w-full rounded-md">
                                <Image
                                  src={prepCenter?.logo}
                                  alt={prepCenter?.name}
                                  layout="fill"
                                  objectFit="contain"
                                  className="rounded-md"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="h-28 w-28 sm:h-40 sm:w-40 rounded-md bg-white p-1 sm:-ml-1 sm:mr-0 mx-auto">
                              <div className="bg-gray-300 w-full h-full rounded-sm flex">
                                <div className="relative h-1/2 w-1/2 m-auto">
                                  <Image
                                    src="/images/icon-light.svg"
                                    alt={prepCenter?.name}
                                    layout="fill"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="mt-4 text-center sm:mt-1 sm:pt-1 sm:text-left">
                          <div className="flex items-center justify-center sm:justify-start">
                            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl flex items-center">
                              {prepCenter?.name}
                              {prepCenter?.verified && (
                                <>
                                  <FontAwesomeIcon
                                    icon={["fas", "shield-check"]}
                                    className="text-lg text-vendrive-base ml-2"
                                  />
                                  <span className="text-sm ml-1 text-slate-600 font-medium">
                                    Verified
                                  </span>
                                </>
                              )}
                            </h1>

                            {prepCenter?.ownerId == null && (
                              <span className="text-gray-500 text-base ml-2 flex items-center">
                                <FontAwesomeIcon
                                  icon={["fas", "circle"]}
                                  className="text-[7px] text-gray-300 mr-2"
                                />
                                Unclaimed
                              </span>
                            )}
                          </div>

                          <div className="flex items-center justify-center sm:justify-start mt-1">
                            {[0, 1, 2, 3, 4].map((rating) => (
                              <StarIcon
                                key={rating}
                                className={classNames(
                                  Math.round(prepCenter?.averageRating ?? 0) >
                                    rating
                                    ? "text-yellow-400"
                                    : "text-gray-300",
                                  "h-5 w-5 flex-shrink-0"
                                )}
                                aria-hidden="true"
                              />
                            ))}
                            <span className="text-gray-500 text-sm ml-2">
                              {prepCenter?.reviewCount
                                ? `${prepCenter?.averageRating?.toFixed(2)} (${
                                    prepCenter?.reviewCount
                                  } Reviews)`
                                : "No reviews"}
                            </span>
                          </div>

                          <p className="mt-2 text-sm font-medium text-gray-600">
                            {prepCenter?.tagline}
                          </p>
                        </div>
                      </div>

                      <div className="mt-5 flex flex-col justify-center sm:mt-0 gap-3">
                        <div className="flex justify-center sm:justify-end gap-3">
                          <a
                            href={`mailto:${prepCenter?.email}`}
                            className={
                              prepCenter?.email
                                ? "flex justify-center items-center px-4 py-2 whitespace-pre border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                : "hidden"
                            }
                          >
                            Email
                          </a>
                          <a
                            href={formatWebsite(prepCenter?.website) as string}
                            target="_blank"
                            rel="noreferrer"
                            className={
                              prepCenter?.website
                                ? "flex justify-center items-center px-4 py-2 whitespace-pre border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                : "hidden"
                            }
                          >
                            Visit website
                          </a>
                        </div>

                        <div>
                          <a
                            href={`tel:${formatPhone(prepCenter?.phone)}`}
                            className={
                              prepCenter?.phone &&
                              formatPhone(prepCenter?.phone)
                                ? "flex items-center justify-center sm:justify-end text-base font-medium whitespace-pre rounded-md text-vendrive-600 hover:text-vendrive-500 md:text-lg"
                                : "hidden"
                            }
                          >
                            <FontAwesomeIcon
                              icon={["fas", "phone"]}
                              className="mr-2"
                            />
                            {formatPhone(prepCenter?.phone)}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Turnaround time and liftgate requirement */}
                  <div
                    className={classNames(
                      "border-t border-gray-200 bg-gray-50 grid grid-cols-1 divide-y divide-gray-200 sm:divide-y-0 sm:divide-x",
                      prepCenter?.liftgateRequired == null
                        ? "sm:grid-cols-1"
                        : "sm:grid-cols-2"
                    )}
                  >
                    <div className="px-6 py-5 text-sm font-medium text-center">
                      <span className="text-gray-900">
                        {prepCenter?.turnaround &&
                        prepCenter?.turnaround != Turnaround.TurnaroundUnknown
                          ? turnaroundTimes[
                              prepCenter?.turnaround as Turnaround
                            ].title
                          : ""}
                      </span>{" "}
                      <span className="text-gray-600">
                        {!prepCenter?.turnaround ||
                        prepCenter?.turnaround === Turnaround.TurnaroundUnknown
                          ? "Unknown Turnaround Time"
                          : "Hour Turnaround Time"}
                      </span>
                    </div>

                    {prepCenter?.liftgateRequired != null && (
                      <div className="px-6 py-5 text-sm font-medium text-center">
                        <span className="text-gray-600">Liftgate</span>{" "}
                        <span className="text-gray-900">
                          {prepCenter?.liftgateRequired
                            ? "Required"
                            : "Not Required"}
                        </span>{" "}
                        <span className="text-gray-600">
                          for Pallet Shipments
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Description */}
              <section
                aria-labelledby="supported-businesses-title"
                className={prepCenter?.description?.length ? "" : "hidden"}
              >
                <div className="rounded-lg bg-white overflow-hidden shadow">
                  <div className="pt-6 pb-7 px-6">
                    <h2
                      className="text-lg font-medium text-gray-900"
                      id="supported-businesses-title"
                    >
                      About
                    </h2>

                    <p className="mt-4 text-sm text-gray-500 whitespace-pre-wrap">
                      {prepCenter?.description}
                    </p>
                  </div>
                </div>
              </section>

              {/* Reviews */}
              <section aria-labelledby="reviews-title">
                <div className="rounded-lg bg-white shadow">
                  <div className="pb-7 px-6">
                    <div className="pt-6 bg-white flex items-center sticky top-0">
                      <h2
                        className="text-lg font-medium text-gray-900 flex-auto"
                        id="reviews-title"
                      >
                        Reviews
                      </h2>

                      <div className="mt-0 sm:ml-16 flex-none">
                        {authUser && myReview == null ? (
                          <button
                            type="button"
                            onClick={() => setCreateReviewModalOpen(true)}
                            className={classNames(
                              "inline-flex items-center justify-center rounded-md border border-transparent bg-vendrive-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-vendrive-700 focus:outline-none focus:ring-2 focus:ring-vendrive-500 focus:ring-offset-2 sm:w-auto",
                              dbUser?.prep_center_id === prepCenter?.id // Can't review your own prep center
                                ? "hidden"
                                : ""
                            )}
                          >
                            Write a review
                          </button>
                        ) : authUser && myReview != null ? (
                          <button
                            type="button"
                            onClick={() => setUpdateReviewModalOpen(true)}
                            className={classNames(
                              "inline-flex items-center justify-center rounded-md border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-vendrive-500 focus:ring-offset-2 sm:w-auto",
                              dbUser?.prep_center_id === prepCenter?.id // Can't review your own prep center
                                ? "hidden"
                                : ""
                            )}
                          >
                            Edit review
                          </button>
                        ) : (
                          <Link href="/signup">
                            <a className="inline-flex items-center justify-center rounded-md border border-transparent bg-vendrive-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-vendrive-700 focus:outline-none focus:ring-2 focus:ring-vendrive-500 focus:ring-offset-2 sm:w-auto">
                              Write a review
                            </a>
                          </Link>
                        )}
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="-my-10">
                        {!prepCenter?.reviews?.length && (
                          <p className="py-10 text-sm text-gray-500">
                            {dbUser?.prep_center_id === prepCenter?.id
                              ? "Request reviews from your clients to rank higher!"
                              : "Be the first to write a review!"}
                          </p>
                        )}

                        {prepCenter?.reviews
                          ?.sort(
                            (a, b) =>
                              b.created_at.getTime() - a.created_at.getTime()
                          )
                          .map((review, reviewIdx) => (
                            <div
                              key={review.userId + review.prepCenterId}
                              className="flex text-sm text-gray-500 space-x-4"
                            >
                              <div className="flex-none py-10">
                                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-400">
                                  <span className="font-medium leading-none text-white">
                                    {getInitials(review)}
                                  </span>
                                </span>
                              </div>

                              <div
                                className={classNames(
                                  reviewIdx === 0
                                    ? ""
                                    : "border-t border-gray-200",
                                  "flex-1 py-10"
                                )}
                              >
                                <h3 className="font-medium text-gray-900">
                                  {review.reviewerFirstName
                                    ? `${review.reviewerFirstName}${
                                        review.reviewerLastName
                                          ? ` ${review.reviewerLastName.charAt(
                                              0
                                            )}.`
                                          : ""
                                      }`
                                    : review.reviewerName}
                                </h3>
                                <p>
                                  <time dateTime={review.created_at.toString()}>
                                    {review.created_at.toLocaleDateString(
                                      "en-US",
                                      {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                      }
                                    )}
                                  </time>
                                </p>

                                <div className="flex items-center mt-4">
                                  {[0, 1, 2, 3, 4].map((rating) => (
                                    <StarIcon
                                      key={rating}
                                      className={classNames(
                                        review.rating > rating
                                          ? "text-yellow-400"
                                          : "text-gray-300",
                                        "h-5 w-5 flex-shrink-0"
                                      )}
                                      aria-hidden="true"
                                    />
                                  ))}
                                </div>
                                <p className="sr-only">
                                  {review.rating} out of 5 stars
                                </p>

                                <p
                                  className={
                                    review.review
                                      ? "mt-4 prose prose-sm max-w-none text-gray-500 whitespace-pre-wrap"
                                      : "hidden"
                                  }
                                >
                                  {review.review}
                                </p>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className="sticky top-3 grid grid-cols-1 gap-4">
              {/* Supported models */}
              <section aria-labelledby="supported-businesses-title">
                <div className="rounded-lg bg-white overflow-hidden shadow">
                  <div className="pt-6 pb-8 px-6">
                    <h2
                      className="text-base font-medium text-gray-900"
                      id="supported-businesses-title"
                    >
                      Supported businesses
                    </h2>

                    <div className="mt-6 flex">
                      {!prepCenter?.supportedModels?.length && (
                        <p className="text-sm text-gray-500 -my-2">
                          None listed.
                        </p>
                      )}

                      <div className="flex flex-row gap-2 flex-wrap">
                        {prepCenter?.supportedModels?.map((model) => (
                          <span
                            key={model}
                            className={classNames(
                              "inline-flex items-center px-2.5 py-1.5 rounded-md text-sm font-medium",
                              supportedModelDetails[model].color
                            )}
                          >
                            {supportedModelDetails[model].title}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Locations */}
              <section aria-labelledby="locations-title">
                <div className="rounded-lg bg-white overflow-hidden shadow">
                  <div className="pt-6 pb-8 px-6">
                    <h2
                      className="text-base font-medium text-gray-900"
                      id="locations-title"
                    >
                      Locations
                    </h2>
                    <div className="flow-root mt-6">
                      {!prepCenter?.locations?.length && (
                        <p className="text-sm text-gray-500 mb-3 -mt-2">
                          None listed.
                        </p>
                      )}

                      <ul
                        role="list"
                        className="-my-5 divide-y divide-gray-200"
                      >
                        {prepCenter?.locations?.map((location, i) => (
                          <li key={location.id} className="py-4">
                            <div className="flex items-center space-x-4">
                              <div className="flex-shrink-0">
                                <LocationMarkerIcon className="h-7 w-7 text-gray-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {location?.name ||
                                    `${prepCenter?.name} Location`}
                                </p>
                                <p className="text-sm text-gray-500 truncate">
                                  {getLocationString(location)}
                                </p>
                              </div>
                              <div>
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${location.latitude}%2C${location.longitude}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center shadow-sm px-2.5 py-0.5 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50"
                                >
                                  View
                                </a>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Social profiles */}
              <section aria-labelledby="social-profiles-title">
                <div className="rounded-lg bg-white overflow-hidden shadow">
                  <div className="p-6">
                    <h2
                      className="text-base font-medium text-gray-900"
                      id="social-profiles-title"
                    >
                      Social profiles
                    </h2>

                    <div className="mt-4 flex gap-6 md:order-2">
                      {!prepCenter?.linkedin &&
                        !prepCenter?.facebook &&
                        !prepCenter?.instagram &&
                        !prepCenter?.twitter &&
                        !prepCenter?.youtube && (
                          <p className="text-sm text-gray-500">None listed.</p>
                        )}

                      <a
                        href={`https://www.linkedin.com/company/${prepCenter?.linkedin}/`}
                        target="_blank"
                        rel="noreferrer"
                        className={
                          prepCenter?.linkedin
                            ? "text-gray-400 hover:text-gray-500"
                            : "hidden"
                        }
                      >
                        <span className="sr-only">Linkedin</span>
                        <FontAwesomeIcon
                          icon={["fab", "linkedin"]}
                          className="text-xl h-6 w-6"
                        />
                      </a>

                      <a
                        href={`https://www.facebook.com/${prepCenter?.facebook}`}
                        target="_blank"
                        rel="noreferrer"
                        className={
                          prepCenter?.facebook
                            ? "text-gray-400 hover:text-gray-500"
                            : "hidden"
                        }
                      >
                        <span className="sr-only">Facebook</span>
                        <FontAwesomeIcon
                          icon={["fab", "facebook"]}
                          className="text-xl h-6 w-6"
                        />
                      </a>

                      <a
                        href={`https://www.instagram.com/${prepCenter?.instagram}/`}
                        target="_blank"
                        rel="noreferrer"
                        className={
                          prepCenter?.instagram
                            ? "text-gray-400 hover:text-gray-500"
                            : "hidden"
                        }
                      >
                        <span className="sr-only">Instagram</span>
                        <FontAwesomeIcon
                          icon={["fab", "instagram"]}
                          className="text-xl h-6 w-6"
                        />
                      </a>

                      <a
                        href={`https://twitter.com/${prepCenter?.twitter}`}
                        target="_blank"
                        rel="noreferrer"
                        className={
                          prepCenter?.twitter
                            ? "text-gray-400 hover:text-gray-500"
                            : "hidden"
                        }
                      >
                        <span className="sr-only">Twitter</span>
                        <FontAwesomeIcon
                          icon={["fab", "twitter"]}
                          className="text-xl h-6 w-6"
                        />
                      </a>

                      <a
                        href={`https://www.youtube.com/c/${prepCenter?.youtube}/`}
                        target="_blank"
                        rel="noreferrer"
                        className={
                          prepCenter?.youtube
                            ? "text-gray-400 hover:text-gray-500"
                            : "hidden"
                        }
                      >
                        <span className="sr-only">YouTube</span>
                        <FontAwesomeIcon
                          icon={["fab", "youtube"]}
                          className="text-xl h-6 w-6"
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </section>

              {/* Manage as admin */}
              {dbUser?.is_admin && (
                <section aria-labelledby="admin-manage-title">
                  <div className="rounded-lg bg-white overflow-hidden shadow">
                    <div className="p-6">
                      <Link href={`/manage/${prepCenter?.id}`} passHref>
                        <a className="flex w-full justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                          Manage as admin
                        </a>
                      </Link>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>

        <ConfirmationModal
          open={claimModalOpen}
          setOpen={setClaimModalOpen}
          title="Claim prep center"
          saveText="Claim"
          handle={handleClaimPrepCenter}
        >
          <p className="text-sm text-gray-500">
            Is this your prep center that you&apos;d like to manage? You&apos;ll
            be able to make changes to your profile, and manage locations and
            reviews.
          </p>
        </ConfirmationModal>
        <CreateReviewModal
          open={createReviewModalOpen}
          setOpen={setCreateReviewModalOpen}
          prepCenterId={prepCenterId as string}
          onCreate={() => doGetPrepCenter()}
        />
        <UpdateReviewModal
          open={updateReviewModalOpen}
          setOpen={setUpdateReviewModalOpen}
          review={myReview}
          onCreate={() => doGetPrepCenter()}
        />
      </main>
    </>
  );
}

Home.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout>{page}</MainLayout>;
};
